import { useCallback, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react';

import { animations } from '../../../config/animations';
import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { Loader } from '../../Loader/Loader';
import { Overlay } from '../../Overlay/Overlay';
import type { IDialogProps } from '../types';

import classes from './Dialog.module.scss';
import { DialogContent } from './DialogContent';
import { DialogHeader } from './DialogHeader';

const OWN_ANIMATION_STEPS = 2;

export const DialogUiRedesign = observer(
  ({
    className,
    classNameBody,
    classNameBackdrop,
    classNameOverlay,
    classNameParent,
    classNameTitle,
    classNameHeader,
    isOpen,
    title,
    children,
    onClose,
    onArrow,
    loading,
    onBackdropClick,
    onAnimationEnd,
    disableBackdropClick,
    disableLoader,
    titleDataTestId,
    dataTestId,
    backBtnDataTestId,
    closeBtnDataTestId,
    onFullyOpened,
    headerLeftElement,
    tooltipContent,
    classNameHeaderDecorationsWrapper,
  }: IDialogProps) => {
    const handleKeydown = useCallback(
      (event: KeyboardEvent) => {
        if (isOpen && onClose && event.key === 'Escape') {
          onClose();
        }
      },
      [isOpen, onClose],
    );

    const childAnimationsCountRef = useRef<number | null>(null);
    const animationStepsPassed = useRef(0);
    const ready = useRef(false);

    useHiddenBodyOverflow(Boolean(isOpen));

    const handleAnimationPart = () => {
      const childAnimationsCount = childAnimationsCountRef.current;

      if (ready.current || childAnimationsCount == null || typeof onFullyOpened !== 'function') {
        return;
      }

      if (++animationStepsPassed.current === OWN_ANIMATION_STEPS + childAnimationsCount) {
        ready.current = true;

        return onFullyOpened();
      }
    };

    useEffect(() => {
      document.addEventListener('keydown', handleKeydown, false);

      return () => {
        document.removeEventListener('keydown', handleKeydown, false);
      };
    }, [handleKeydown]);

    const contentRef = useCallback((instance: HTMLDivElement | null) => {
      if (instance) {
        childAnimationsCountRef.current = instance.getAnimations({ subtree: true }).length;
      }
    }, []);

    return (
      <AnimatePresence initial={isOpen}>
        <Overlay
          backdropClassName={classNames(classes.dialogOverlayBackdrop, classNameBackdrop)}
          className={classNames(classes.dialogOverlay, classNameOverlay)}
          disableBackdropClick={disableBackdropClick}
          onClick={onBackdropClick}
        >
          <motion.div
            key="modal"
            ref={contentRef}
            animate={animations.scale.visible}
            className={classNames(classes.dialogWrapper, classNameParent)}
            exit={animations.scale.hidden}
            initial={animations.scale.hidden}
            role="dialog"
            onAnimationComplete={handleAnimationPart}
            onTransitionEnd={handleAnimationPart}
          >
            <div
              className={classNames(classes.dialog, className)}
              data-testid={dataTestId}
              onAnimationEnd={() => {
                if (typeof onAnimationEnd === 'function') {
                  onAnimationEnd();
                }

                handleAnimationPart();
              }}
            >
              {loading && !disableLoader && (
                <div className={classes.dialogBodyLoader}>
                  <Loader />
                </div>
              )}
              <DialogHeader
                className={classNameHeader}
                closeBtnDataTestId={closeBtnDataTestId}
                decorationsWrapperClassName={classNameHeaderDecorationsWrapper}
                goBackBtnDataTestId={backBtnDataTestId}
                headerLeftElement={headerLeftElement}
                title={title}
                titleClassName={classNameTitle}
                titleDataTestId={titleDataTestId}
                tooltipContent={tooltipContent}
                onClose={onClose}
                onGoBack={onArrow}
              />
              <DialogContent className={classNameBody}>{children}</DialogContent>
            </div>
          </motion.div>
        </Overlay>
      </AnimatePresence>
    );
  },
);
